import React, { useState } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { useBuilderStore } from '../../../store/checkoutBuilder/BuilderContext';
import type { RowNode, ColumnNode } from '../../../store/checkoutBuilder/types';
import StageZoneCanvas from './StageZoneCanvas';

// Ícones inline agora usam Remix Icons


interface LegacyStageProps {
    globalSettings?: {
        fontFamily: string;
        backgroundColor: string;
        backgroundImage: string;
        fixedBackground: boolean;
        repeatBackground: boolean;
        expandBackground: boolean;
    };
}

const LegacyStage: React.FC<LegacyStageProps> = ({ globalSettings }) => {
    const { state, selectNode, deleteNode, duplicateNode } = useBuilderStore();
    const { document, ui } = state;
    const [confirmDeleteRowId, setConfirmDeleteRowId] = useState<string | null>(null);
    const isMobile = ui?.breakpoint === 'mobile';

    // Renderiza uma linha com hover em largura total
    const renderRowWithControls = (rowId: string, idx: number) => {
        const node = document.nodes[rowId];
        if (!node || node.type !== 'row') return null;

        return (
            <Draggable key={rowId} draggableId={`row:${rowId}`} index={idx}>
                {(dragProvided) => (
                    <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        className="group relative mb-3 last:mb-0 w-full hover:border hover:border-gray-200 rounded-md"
                    >
                        <StageZoneCanvas rowId={rowId} emptyHint="Arraste componentes aqui" />
                        {/* Ícones da linha - aparecem no hover da linha inteira apenas quando não há componente selecionado */}
                        {!state.selectionId && (
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-40 pointer-events-none" onClick={(e) => e.stopPropagation()}>
                                <div className="pointer-events-auto flex gap-1">
                                    <button
                                        onClick={() => {
                                            const row = document.nodes[rowId];
                                            if (!row || row.type !== 'row') return;

                                            // Verificar se a linha tem componentes
                                            const firstColId = row.children?.[0];
                                            const col = firstColId ? document.nodes[firstColId] : undefined;

                                            if (col && col.type === 'column' && col.children.length > 0) {
                                                // Tem componentes, mostrar confirmação
                                                setConfirmDeleteRowId(rowId);
                                            } else {
                                                // Não tem componentes, excluir diretamente
                                                deleteNode(rowId);
                                            }
                                        }}
                                        title="Excluir linha"
                                        aria-label="Excluir linha"
                                        className="p-1.5 rounded-full bg-white border shadow text-gray-700 hover:bg-gray-50"
                                    >
                                        <i className="ri-delete-bin-line text-sm"></i>
                                    </button>
                                    <button
                                        onClick={() => duplicateNode(rowId)}
                                        title="Duplicar linha"
                                        aria-label="Duplicar linha"
                                        className="p-1.5 rounded-full bg-white border shadow text-gray-700 hover:bg-gray-50"
                                    >
                                        <i className="ri-file-copy-line text-sm"></i>
                                    </button>
                                    <button
                                        onClick={() => {
                                            // Selecionar a linha primeiro
                                            selectNode(rowId);
                                            // Depois disparar evento para mudar para a aba de linhas
                                            const event = new CustomEvent('checkout-builder:show-lines-tab', {
                                                detail: { rowId }
                                            });
                                            window.dispatchEvent(event);
                                        }}
                                        title="Configurar linha"
                                        aria-label="Configurar linha"
                                        className="p-1.5 rounded-full bg-white border shadow text-gray-700 hover:bg-gray-50"
                                    >
                                        <i className="ri-settings-3-line text-sm"></i>
                                    </button>
                                    <button
                                        title="Mover linha"
                                        aria-label="Mover linha"
                                        className="p-1.5 rounded-full bg-white border shadow text-gray-700 hover:bg-gray-50 cursor-grab active:cursor-grabbing"
                                    >
                                        <i className="ri-drag-move-2-line text-sm"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Draggable>
        );
    };

    return (
        <div
            className={`rounded-lg p-8 min-h-[600px] ${globalSettings?.backgroundColor && globalSettings.backgroundColor !== '#ffffff'
                ? ''
                : 'border border-gray-200 dark:border-gray-800'
                }`}
            style={{
                fontFamily: globalSettings?.fontFamily || 'inherit',
                backgroundColor: globalSettings?.backgroundColor || '#f3f4f6'
            }}
            onClick={() => selectNode(null)}
        >
            <div className="w-full">
                {/* Zona principal de drop - área independente para desktop e mobile */}
                <Droppable droppableId={isMobile ? "mobile-zone:0" : "zone:0"} type="rows">
                    {(provided, snapshot) => {
                        // Usar root diferente baseado no breakpoint
                        let currentRoot;
                        if (isMobile) {
                            // Inicializar mobileRoot se não existir
                            if (!(document as any).mobileRoot) {
                                (document as any).mobileRoot = [];

                                // Criar uma linha inicial vazia para o mobile (igual ao desktop)
                                const mobileRowId = 'mobile-initial-row';
                                const mobileColId = 'mobile-initial-col';

                                // Criar linha inicial
                                const mobileRow: RowNode = {
                                    id: mobileRowId,
                                    parentId: null,
                                    type: 'row',
                                    props: { gap: 16, marginY: 16 },
                                    children: [mobileColId],
                                };

                                // Criar coluna inicial
                                const mobileCol: ColumnNode = {
                                    id: mobileColId,
                                    parentId: mobileRowId,
                                    type: 'column',
                                    props: { widthByBp: { desktop: 12, tablet: 12, mobile: 12 } },
                                    children: [],
                                };

                                // Adicionar ao documento
                                document.nodes[mobileRowId] = mobileRow;
                                document.nodes[mobileColId] = mobileCol;

                                // Adicionar linha ao mobileRoot
                                (document as any).mobileRoot.push(mobileRowId);
                            }
                            currentRoot = (document as any).mobileRoot;
                        } else {
                            currentRoot = document.root;
                        }

                        return (
                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className="w-full mb-6"
                            >
                                {/* Container azul pontilhado - só aparece quando vazio E está arrastando */}
                                {currentRoot.length === 0 && snapshot.isDraggingOver && (
                                    <div className="w-full min-h-[120px] border-2 border-dashed rounded-lg transition-all p-6 border-blue-400 bg-blue-50">
                                        <div className="text-center text-blue-600 font-medium">
                                            Arraste componentes aqui
                                        </div>
                                    </div>
                                )}

                                {/* Linhas existentes */}
                                {currentRoot.map((rowId: string, idx: number) => renderRowWithControls(rowId, idx))}

                                {provided.placeholder}
                            </div>
                        );
                    }}
                </Droppable>

                {/* Linha central fixa do checkout - SEMPRE VISÍVEL com placeholder azul e ícones específicos */}
                <div className="mt-6 mb-8">
                    <div className="group relative rounded-lg p-2 hover:border hover:border-gray-200">
                        {/* Botão de configuração - só aparece no desktop */}
                        {!isMobile && (
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-40" onClick={(e) => e.stopPropagation()}>
                                <button
                                    onClick={() => {
                                        // Abrir configurações da linha do checkout no painel lateral
                                        selectNode('checkout-row-config');
                                    }}
                                    title="Configurar layout da linha (1 ou 2 colunas)"
                                    aria-label="Configurar linha do checkout"
                                    className="p-1.5 rounded-full bg-white border shadow text-gray-700 hover:bg-gray-50"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="3"></circle>
                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                                    </svg>
                                </button>
                            </div>
                        )}
                        {(() => {
                            // Obter o layout de forma segura
                            const checkoutLayout = (document.meta as any)?.checkoutLayout || 'two-columns';
                            const showSideColumn = checkoutLayout === 'two-columns' && !isMobile;

                            return (
                                <div className={`${isMobile ? 'flex flex-col' : 'flex'} w-full max-w-7xl mx-auto ${showSideColumn ? 'gap-6' : 'justify-center'}`}>
                                    {/* Bloco do checkout */}
                                    <div className={`relative flex flex-col items-center bg-white ${isMobile ? 'rounded-lg shadow-md p-4 w-full' : 'rounded-xl shadow-lg p-6'} ${showSideColumn ? 'flex-1 max-w-[650px]' : isMobile ? '' : 'w-[650px] mx-auto'}`}>
                                        <img
                                            src={isMobile ? "/images/payment/mobile_checkout_placeholder.webp" : "/images/payment/checkout_placeholder.webp"}
                                            alt={isMobile ? "Mobile Checkout Placeholder" : "Checkout Placeholder"}
                                            className="rounded w-full pointer-events-none"
                                            draggable={false}
                                        />
                                        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black bg-opacity-60 text-white px-4 py-2 rounded text-center pointer-events-none select-none">
                                            Checkout<br />Será exibido aqui
                                        </span>
                                    </div>

                                    {/* Zona lateral - aparece apenas no desktop quando configurado para 2 colunas */}
                                    {showSideColumn && (
                                        <div className="w-65 flex-shrink-0 h-full">
                                            {/* Usamos o StageZoneCanvas para a área lateral também */}
                                            <div className="group relative hover:border hover:border-gray-200 rounded-md">
                                                {/* Criamos uma linha e coluna lateral se não existir */}
                                                {(() => {
                                                    const areaRowId = 'side-area-row';
                                                    const areaColId = 'side-area-col';

                                                    // Verificamos se já existe a linha
                                                    if (!document.nodes[areaRowId]) {
                                                        // Criar linha para a área
                                                        const areaRow: RowNode = {
                                                            id: areaRowId,
                                                            parentId: null,
                                                            type: 'row',
                                                            props: { gap: 16, marginY: 16 },
                                                            children: [areaColId],
                                                        };

                                                        // Criar coluna para a área
                                                        const areaCol: ColumnNode = {
                                                            id: areaColId,
                                                            parentId: areaRowId,
                                                            type: 'column',
                                                            props: { widthByBp: { desktop: 12, tablet: 12, mobile: 12 } },
                                                            children: [],
                                                        };

                                                        // Adicionar ao documento
                                                        document.nodes[areaRowId] = areaRow;
                                                        document.nodes[areaColId] = areaCol;
                                                    }

                                                    return (
                                                        <StageZoneCanvas rowId="side-area-row" emptyHint="Arraste componentes aqui" fullHeight />
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* Área de footer mobile - aparece apenas no mobile QUANDO desktop tem coluna lateral (2 colunas) */}
                {isMobile && ((document.meta as any)?.checkoutLayout || 'two-columns') === 'two-columns' && (
                    <div className="mt-6">
                        <div className="group relative hover:border hover:border-gray-200 rounded-md">
                            {(() => {
                                const areaRowId = 'mobile-footer-row';
                                const areaColId = 'mobile-footer-col';

                                // Verificamos se já existe a linha
                                if (!document.nodes[areaRowId]) {
                                    // Criar linha para a área
                                    const areaRow: RowNode = {
                                        id: areaRowId,
                                        parentId: null,
                                        type: 'row',
                                        props: { gap: 16, marginY: 16 },
                                        children: [areaColId],
                                    };

                                    // Criar coluna para a área
                                    const areaCol: ColumnNode = {
                                        id: areaColId,
                                        parentId: areaRowId,
                                        type: 'column',
                                        props: { widthByBp: { desktop: 12, tablet: 12, mobile: 12 } },
                                        children: [],
                                    };

                                    // Adicionar ao documento
                                    document.nodes[areaRowId] = areaRow;
                                    document.nodes[areaColId] = areaCol;
                                }

                                return (
                                    <StageZoneCanvas rowId="mobile-footer-row" emptyHint="Componentes adicionais (footer)" />
                                );
                            })()}
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de confirmação para exclusão de linha com componentes */}
            {confirmDeleteRowId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
                        <h3 className="text-lg font-medium mb-4">Confirmar exclusão</h3>
                        <p className="text-gray-600 mb-6">Esta linha contém componentes. Tem certeza que deseja excluí-la?</p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setConfirmDeleteRowId(null)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => {
                                    if (confirmDeleteRowId) {
                                        deleteNode(confirmDeleteRowId);
                                        setConfirmDeleteRowId(null);
                                    }
                                }}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                                Excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LegacyStage;
